/**
 * 🧮 원가분석표 목록 + 자동 저장 — 원가분석 탭과 견적서 작성 창의 🧮 원가분석 창이 같이 쓴다 (v2.248.0 에서 탭에서 떼어 냄)
 *
 * 값은 react-query 캐시(COST_SHEETS_QK)에 두고, 고치면 캐시를 바로 바꾼 뒤 0.7초 뒤 「마지막 고침」만 서버로 보낸다.
 * ⚠입력칸은 캐시 값을 바로 받지 말 것 — 캐시 알림은 setTimeout(0) 뒤라 한글이 갈라진다(v2.247.1 · CostUi TextCell).
 * ⚠줄을 고칠 땐 화면 변수가 아니라 **캐시의 최신 줄**(currentLines)에서 출발한다 — 같은 순간 두 칸을 고치면 앞 고침이 사라졌다(10-09).
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient, type QueryClient, type UseQueryResult } from '@tanstack/react-query';
import * as api from '../../lib/cost-sheet-api';
import type { CostSheet, CostSheetInput } from '../../lib/cost-sheet-api';
import type { CostLine } from '../../lib/cost-sheet-calc';

export const COST_SHEETS_QK = ['price-list', 'cost-sheets'] as const;
const SAVE_DELAY_MS = 700;

export type SaveState = { id: number; status: 'saving' | 'saved' | 'error'; at: number; message?: string } | null;

export interface CostSheetStore {
  query: UseQueryResult<CostSheet[]>;
  sheets: CostSheet[];
  saveState: SaveState;
  /** 캐시를 바로 바꾸고 0.7초 뒤 서버로 */
  patchSheet: (id: number, patch: Partial<CostSheetInput>) => void;
  /** 기다리던 고침을 바로 보낸다(끝날 때까지 기다림) */
  flush: (id: number) => Promise<void>;
  flushAll: () => void;
  /** 그 표의 고침을 버린다(지운 표) */
  forget: (id: number) => void;
  currentLines: (id: number) => CostLine[];
}

export function useCostSheets(opts: { enabled?: boolean } = {}): CostSheetStore {
  const qc = useQueryClient();
  // ⚠창을 다시 볼 때 자동 재조회를 끈다 — 자동 저장 직전의 고침(0.7초)이 서버 값으로 덮이지 않게
  const query = useQuery({
    queryKey: COST_SHEETS_QK, queryFn: () => api.listCostSheets(), staleTime: 60_000, refetchOnWindowFocus: false,
    enabled: opts.enabled ?? true,
  });
  const sheets = useMemo(() => query.data ?? [], [query.data]);

  const timers = useRef<Map<number, ReturnType<typeof setTimeout>>>(new Map());
  const pending = useRef<Map<number, Partial<CostSheetInput>>>(new Map());
  const [saveState, setSaveState] = useState<SaveState>(null);

  const flush = async (id: number): Promise<void> => {
    const t = timers.current.get(id);
    if (t) { clearTimeout(t); timers.current.delete(id); }
    const p = pending.current.get(id);
    if (!p) return;
    pending.current.delete(id);
    setSaveState({ id, status: 'saving', at: Date.now() });
    try {
      const saved = await api.updateCostSheet(id, p);
      // 서버가 정한 칸만 받아 적는다 — 그 사이 화면에서 더 고친 줄을 서버 값으로 덮지 않게
      qc.setQueryData<CostSheet[]>(COST_SHEETS_QK, (old) => (old ?? []).map((s) => (s.id === id
        ? { ...s, updatedAt: saved.updatedAt, updatedBy: saved.updatedBy, quoteId: saved.quoteId, quoteNo: saved.quoteNo,
            salesId: saved.salesId, salesProjectName: saved.salesProjectName, salesEndUser: saved.salesEndUser, salesProposalPrice: saved.salesProposalPrice }
        : s)));
      setSaveState({ id, status: 'saved', at: Date.now() });
    } catch (e) {
      // 못 보낸 고침은 다시 쌓아 둔다(그 뒤 고친 것이 있으면 그쪽이 이긴다) — [다시 저장] 으로 보낸다
      pending.current.set(id, { ...p, ...(pending.current.get(id) ?? {}) });
      setSaveState({ id, status: 'error', at: Date.now(), message: (e as Error).message });
    }
  };
  const patchSheet = (id: number, patch: Partial<CostSheetInput>): void => {
    qc.setQueryData<CostSheet[]>(COST_SHEETS_QK, (old) => (old ?? []).map((s) => (s.id === id ? { ...s, ...patch } : s)));
    pending.current.set(id, { ...(pending.current.get(id) ?? {}), ...patch });
    const prev = timers.current.get(id);
    if (prev) clearTimeout(prev);
    timers.current.set(id, setTimeout(() => { void flush(id); }, SAVE_DELAY_MS));
  };
  const flushAll = (): void => { for (const id of Array.from(timers.current.keys())) void flush(id); };
  const forget = (id: number): void => {
    const t = timers.current.get(id);
    if (t) clearTimeout(t);
    timers.current.delete(id);
    pending.current.delete(id);
  };
  const currentLines = (id: number): CostLine[] =>
    qc.getQueryData<CostSheet[]>(COST_SHEETS_QK)?.find((s) => s.id === id)?.lines ?? sheets.find((s) => s.id === id)?.lines ?? [];

  useEffect(() => {
    const ts = timers.current;
    const ps = pending.current;
    // 화면을 떠날 때 기다리던 저장을 바로 보낸다(유실 방지) · 창을 닫으려 하면 묻는다
    const onBeforeUnload = (e: BeforeUnloadEvent) => { if (ps.size > 0) { e.preventDefault(); } };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', onBeforeUnload);
      for (const t of ts.values()) clearTimeout(t);
      for (const [id, p] of ps) api.updateCostSheet(id, p).catch(() => {});
      ts.clear();
      ps.clear();
    };
  }, []);

  return { query, sheets, saveState, patchSheet, flush, flushAll, forget, currentLines };
}

/** 목록 캐시를 서버 목록으로 채워 둔다(아직 안 읽었으면) — 새 표를 앞에 끼우기 전에 · 한 표만 든 목록이 탭에 보이지 않게 */
export async function ensureCostSheets(qc: QueryClient, fresh = false): Promise<CostSheet[]> {
  if (fresh) return qc.fetchQuery({ queryKey: COST_SHEETS_QK, queryFn: () => api.listCostSheets(), staleTime: 0 });
  return qc.ensureQueryData({ queryKey: COST_SHEETS_QK, queryFn: () => api.listCostSheets() });
}
